// BL-177 Step 2e (item 9): alert sounds for the in-app pop-up and bell.
//   * Standard tone (5 + Off) for ordinary alerts; urgent tone (5 + Off, stronger) for critical-severity alerts and
//     red "Act now" alerts.
//   * Volume 0-100 with a Play button on every tone. Defaults: on, Ripple, Beacon, 70 %.
//   * Personal quiet hours mute every sound except critical-severity alerts.
//   * No sound while the person is already reading that conversation (same rule as the pop-up).
//   * Browsers only let a page make sound after the person has clicked or typed on it; until then we stay silent and
//     never throw.
// Push banners use the system sound; the only control there is the "Silent" box (user_sound_settings.push_silent).
// The ten tones are synthesized in-house (scripts/build-alert-tones.mjs): no licensed sound packs.

export const STANDARD_TONES = Object.freeze([
    { id: 'ripple', label: 'Ripple' },
    { id: 'glass', label: 'Glass' },
    { id: 'pebble', label: 'Pebble' },
    { id: 'drift', label: 'Drift' },
    { id: 'spark', label: 'Spark' },
]);
export const URGENT_TONES = Object.freeze([
    { id: 'beacon', label: 'Beacon' },
    { id: 'klaxon', label: 'Klaxon' },
    { id: 'pulse', label: 'Pulse' },
    { id: 'siren', label: 'Siren' },
    { id: 'strike', label: 'Strike' },
]);
export const TONE_OFF = 'off';
/** Fired on window after a person saves their sounds, so the bell picks the new ones up at once. */
export const SOUND_SETTINGS_CHANGED_EVENT = 'ia:sound-settings-changed';

export const SOUND_DEFAULTS = Object.freeze({
    soundEnabled: true,
    standardTone: 'ripple',
    urgentTone: 'beacon',
    volume: 70,
    pushSilent: false,
    quietHours: Object.freeze({ enabled: false, startsAt: '22:00', endsAt: '07:00', timeZone: 'America/New_York' }),
});

const STANDARD_IDS = STANDARD_TONES.map((tone) => tone.id);
const URGENT_IDS = URGENT_TONES.map((tone) => tone.id);

/** my_sound_settings() output (or nothing) -> a complete, valid settings object. Unknown values fall back to defaults. */
export function normalizeSoundSettings(raw) {
    const input = raw && typeof raw === 'object' ? raw : {};
    const volume = Number(input.volume);
    const quiet = input.quietHours && typeof input.quietHours === 'object' ? input.quietHours : {};
    const time = (value, fallback) => (/^\d{2}:\d{2}/.test(String(value || '')) ? String(value).slice(0, 5) : fallback);
    return {
        soundEnabled: input.soundEnabled === undefined ? SOUND_DEFAULTS.soundEnabled : input.soundEnabled === true,
        standardTone: [...STANDARD_IDS, TONE_OFF].includes(input.standardTone) ? input.standardTone : SOUND_DEFAULTS.standardTone,
        urgentTone: [...URGENT_IDS, TONE_OFF].includes(input.urgentTone) ? input.urgentTone : SOUND_DEFAULTS.urgentTone,
        volume: Number.isFinite(volume) ? Math.min(100, Math.max(0, Math.round(volume))) : SOUND_DEFAULTS.volume,
        pushSilent: input.pushSilent === true,
        quietHours: {
            enabled: quiet.enabled === true,
            startsAt: time(quiet.startsAt, SOUND_DEFAULTS.quietHours.startsAt),
            endsAt: time(quiet.endsAt, SOUND_DEFAULTS.quietHours.endsAt),
            timeZone: typeof quiet.timeZone === 'string' && quiet.timeZone ? quiet.timeZone : SOUND_DEFAULTS.quietHours.timeZone,
        },
    };
}

export function toneUrl(id, publicUrl = process.env.PUBLIC_URL || '') {
    return `${publicUrl}/sounds/${id}.wav`;
}

function minutesOfDay(value) {
    const match = /^(\d{2}):(\d{2})/.exec(String(value || ''));
    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/** Minutes after local midnight in a time zone (the company's), at a moment. */
export function localMinutes(now, timeZone) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone }).formatToParts(now);
        return Number(parts.find((part) => part.type === 'hour').value) * 60 + Number(parts.find((part) => part.type === 'minute').value);
    } catch {
        return now.getHours() * 60 + now.getMinutes();
    }
}

/** Same rule as the database's bl177_time_in_window: [start, end), crossing midnight when end < start, equal = empty. */
export function isInQuietHours(now, quiet) {
    if (!quiet || quiet.enabled !== true) return false;
    const start = minutesOfDay(quiet.startsAt);
    const end = minutesOfDay(quiet.endsAt);
    if (start === null || end === null || start === end) return false;
    const local = localMinutes(now, quiet.timeZone || SOUND_DEFAULTS.quietHours.timeZone);
    return start < end ? local >= start && local < end : local >= start || local < end;
}

/**
 * Should this alert make a sound, and which one?
 *   alert: { urgent: boolean (critical severity or red "Act now"), critical: boolean (critical severity), sessionId }
 * Returns { play, tone, volume, reason }.
 */
export function decideAlertSound({ settings, alert = {}, now = new Date(), activeThreadId = '', userActivated = true } = {}) {
    const s = normalizeSoundSettings(settings);
    const none = (reason) => ({ play: false, tone: null, volume: s.volume, reason });
    if (!s.soundEnabled) return none('sound off');
    const tone = alert.urgent ? s.urgentTone : s.standardTone;
    if (tone === TONE_OFF) return none('tone off');
    if (s.volume === 0) return none('volume zero');
    if (!alert.critical && isInQuietHours(now, s.quietHours)) return none('quiet hours');
    const session = String(alert.sessionId || '').toLowerCase();
    if (session && activeThreadId && session === String(activeThreadId).toLowerCase()) return none('already reading this conversation');
    if (!userActivated) return none('page has not been clicked yet');
    return { play: true, tone, volume: s.volume, reason: alert.urgent ? 'urgent' : 'standard' };
}

// --- the browser side -----------------------------------------------------------------------------------------

let activated = false;
let listening = false;
let audioContext = null;
const bufferCache = new Map();
let wantedTones = [];

function audioContextClass() {
    if (typeof window === 'undefined') return null;
    return window.AudioContext || window.webkitAudioContext || null;
}

/**
 * BL-177 Step 2f: Safari (and Chrome's strict mode) only let a page start sound from inside a click or key press. An alert tone
 * starts from a live update, so the first click or key press "unlocks" a Web Audio context for the page; from then on tones
 * play whenever an alert arrives. Safe to call any time: it does nothing without a gesture's permission and never throws.
 */
export function unlockAudio() {
    try {
        const Context = audioContextClass();
        if (!Context) return;
        if (!audioContext) audioContext = new Context();
        if (audioContext.state === 'suspended' && typeof audioContext.resume === 'function') audioContext.resume().catch(() => {});
        preloadTones(wantedTones);
    } catch { /* no Web Audio: the plain audio element is used instead */ }
}

/** True once the person has clicked, tapped or typed on this page (the browser's rule for letting it make sound). */
export function hasUserActivated() {
    if (activated) return true;
    try {
        if (typeof navigator !== 'undefined' && navigator.userActivation?.hasBeenActive) return true;
    } catch { /* older browsers */ }
    return false;
}

export function trackUserActivation(target = typeof window !== 'undefined' ? window : null) {
    if (listening || !target?.addEventListener) return;
    listening = true;
    const mark = () => { activated = true; unlockAudio(); };
    ['pointerdown', 'keydown', 'touchstart', 'click'].forEach((name) => target.addEventListener(name, mark, { capture: true, passive: true }));
}

/** Test hook: forget what this module learned about the page. */
export function resetUserActivation() { activated = false; listening = false; audioContext = null; bufferCache.clear(); wantedTones = []; }

async function toneBuffer(id, publicUrl) {
    if (bufferCache.has(id)) return bufferCache.get(id);
    const pending = (async () => {
        const response = await fetch(toneUrl(id, publicUrl));
        if (!response.ok) throw new Error('tone not found');
        return audioContext.decodeAudioData(await response.arrayBuffer());
    })();
    bufferCache.set(id, pending);
    pending.catch(() => bufferCache.delete(id));
    return pending;
}

/** Fetch and decode tones ahead of time so an alert's tone starts at once. Never throws. */
export function preloadTones(ids = [], { publicUrl } = {}) {
    try {
        wantedTones = ids; // remembered: the context only exists after the person's first click
        if (!audioContext) return;
        ids.filter((id) => [...STANDARD_IDS, ...URGENT_IDS].includes(id)).forEach((id) => { toneBuffer(id, publicUrl).catch(() => {}); });
    } catch { /* the plain audio element is used instead */ }
}

async function playWithWebAudio(id, volume, publicUrl) {
    if (!audioContext || audioContext.state !== 'running') return false;
    const buffer = await toneBuffer(id, publicUrl);
    const source = audioContext.createBufferSource();
    const gain = audioContext.createGain();
    source.buffer = buffer;
    gain.gain.value = Math.min(1, Math.max(0, Number(volume) / 100));
    source.connect(gain);
    gain.connect(audioContext.destination);
    source.start(0);
    return true;
}

const cache = new Map();

/**
 * Plays one tone. Resolves true when the browser started it, false when it was blocked or unavailable. Never throws.
 */
export async function playTone(id, volume = SOUND_DEFAULTS.volume, { AudioCtor = typeof Audio !== 'undefined' ? Audio : null, publicUrl } = {}) {
    if (![...STANDARD_IDS, ...URGENT_IDS].includes(id)) return false;
    // Preferred: the unlocked Web Audio context (works for a tone that starts from a live alert, in Safari too).
    try {
        if (await playWithWebAudio(id, volume, publicUrl)) return true;
    } catch { /* fall back to the plain audio element */ }
    if (!AudioCtor) return false;
    try {
        let audio = cache.get(id);
        if (!audio) {
            audio = new AudioCtor(toneUrl(id, publicUrl));
            audio.preload = 'auto';
            cache.set(id, audio);
        }
        audio.volume = Math.min(1, Math.max(0, Number(volume) / 100));
        audio.currentTime = 0;
        const started = audio.play();
        if (started && typeof started.then === 'function') await started;
        return true;
    } catch {
        return false;
    }
}

/** Tests: drop cached audio elements. */
export function clearToneCache() { cache.clear(); }
